/**
 * Font sizing for the interviewer's message.
 *
 * Messages are revealed word by word in time with the voice, and every word is
 * laid out from the first frame (unrevealed words are only transparent), so the
 * whole message has to fit its box from the start. Long ones, the spoken
 * welcome above all, used to overflow and scroll teleprompter-style instead.
 *
 * `fitFontSize` finds the largest size, on a `step` grid between `min` and
 * `max`, at which `fits` holds. Text only gets taller as it gets bigger, so a
 * binary search is enough: a handful of synchronous layouts per message.
 * When even `min` doesn't fit it returns `min`, and the caller scrolls.
 */
export interface FitFontSizeInput {
  /** Smallest size worth reading, in px. */
  min: number;
  /** The design size for this message, in px; the fit only ever shrinks. */
  max: number;
  /** Lays the text out at `px` and reports whether it fits. */
  fits: (px: number) => boolean;
  /** Resolution of the search, in px. */
  step?: number;
}

export function fitFontSize({ min, max, fits, step = 0.5 }: FitFontSizeInput): number {
  if (!(max > min)) return min;
  if (fits(max)) return max;
  if (!fits(min)) return min;
  // Invariant: fits(lo) && !fits(hi).
  let lo = min;
  let hi = max;
  while (hi - lo > step) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) lo = mid;
    else hi = mid;
  }
  return Math.max(min, Math.floor(lo / step) * step);
}

/**
 * Where the box should scroll so the word being spoken sits in view, for a
 * message too long to fit even at the smallest size. Returns a scrollTop that
 * puts the spoken word's line mid-box, snapped to whole lines so the top edge
 * rests between lines instead of slicing one through its glyphs, and clamped
 * so the last line ends flush with the bottom.
 *
 * `contentHeight` must be the laid-out height of the text. A scroll box's own
 * scrollHeight also counts the entrance offset of words that haven't appeared
 * yet (they rise 10px into place), which made the old teleprompter scroll one
 * notch too far, clip the top line, and snap back when the last word landed.
 */
export interface FollowRevealInput {
  /** Visible height of the scrolling box. */
  clientHeight: number;
  /** Laid-out height of the text inside it. */
  contentHeight: number;
  /** Top of the word being spoken, relative to the top of the text. */
  wordTop: number;
  /** Height of that word's line box. */
  wordHeight: number;
}

export function followScrollTop({
  clientHeight,
  contentHeight,
  wordTop,
  wordHeight,
}: FollowRevealInput): number {
  const max = Math.max(0, contentHeight - clientHeight);
  if (max === 0) return 0;
  const line = wordHeight > 0 ? wordHeight : 1;
  const centered = wordTop + wordHeight / 2 - clientHeight / 2;
  const snapped = Math.round(centered / line) * line;
  return Math.min(max, Math.max(0, snapped));
}
